import React, { useState, useEffect, useCallback, useRef } from "react";
import { PackageSearch, ShoppingCart, Plus, X, List, ArrowDownToLine, ArrowUpFromLine, Pencil, Printer, ScanBarcode, Trash2, Image as ImageIcon } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Image Upload Helper
const handleImageUpload = (e, setFormData) => {
  const file = e.target.files[0];
  if (file) {
    if(file.size > 2000000) { // 2MB limit
      alert("Image is too large. Please select an image under 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setFormData(prev => ({...prev, image_url: reader.result}));
    reader.readAsDataURL(file);
  }
};

const PrintStickerModal = ({ item, onClose }) => {
  const handlePrint = () => {
    window.print();
  };
  return (
    <div className="fixed inset-0 bg-card_bg z-[100] flex flex-col items-center justify-center text-black">
      <div className="print-area flex flex-col items-center justify-center bg-card_bg" style={{ width: '50mm', height: '25mm', padding: '2mm', boxSizing: 'border-box', overflow: 'hidden' }}>
         <h2 className="font-bold text-[10px] uppercase text-center leading-none truncate w-full mb-1">{item.name}</h2>
         <img src={`https://barcodeapi.org/api/128/${item.barcode || item.id}`} alt="barcode" className="h-6 w-full object-contain" />
         <p className="text-[8px] font-mono mt-0.5 leading-none">{item.barcode || item.id}</p>
         <p className="text-[12px] font-bold leading-none mt-1">₹{item.price}</p>
      </div>
      <div className="mt-8 no-print flex gap-4">
         <button onClick={handlePrint} className="bg-primary text-[#111827] px-6 py-2 rounded-md font-bold hover:bg-primary_hover shadow-md">Print Label</button>
         <button onClick={onClose} className="bg-gray-200 text-text_primary px-6 py-2 rounded-md font-bold hover:bg-gray-300 shadow-md">Close</button>
      </div>
      <style>{`
        @media print { 
          body * { visibility: hidden; } 
          .print-area, .print-area * { visibility: visible; } 
          .print-area { position: absolute; left: 0; top: 0; margin: 0; padding: 2mm !important; } 
          @page { size: 50mm 25mm; margin: 0; }
        }
      `}</style>
    </div>
  )
}

const AddInventoryModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({ name: "", stock: 10, price: 1000, barcode: "", image_url: "" });
  
  const generateBarcode = () => {
    const randomCode = 'SVT-' + Math.floor(100000000 + Math.random() * 900000000);
    setFormData({...formData, barcode: randomCode});
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl overflow-y-auto max-h-[90vh]">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-gold transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-gold mb-6">Add Item</h2>
        <form onSubmit={(e) => { e.preventDefault(); onAdd(formData); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Product Name</label>
            <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Product Image (Optional)</label>
            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setFormData)} className="w-full bg-card_bg border border-border_soft rounded-xl p-2 text-text_primary focus:ring-1 focus:ring-primary/50 text-sm" />
            {formData.image_url && <img src={formData.image_url} alt="Preview" className="h-16 mt-3 rounded-md border border-border_soft/50 shadow-sm" />}
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Barcode / SKU</label>
            <div className="flex gap-2">
              <input type="text" value={formData.barcode} onChange={e=>setFormData({...formData, barcode: e.target.value})} placeholder="Scan or enter barcode..." className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary font-mono focus:ring-1 focus:ring-primary/50" />
              <button type="button" onClick={generateBarcode} className="bg-primary/10 text-gold border border-primary/20 px-4 rounded-xl font-bold text-xs shrink-0 hover:bg-primary/20 transition-colors">Auto-Gen</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Initial Reserves</label>
              <input required type="number" min="0" value={formData.stock} onChange={e=>setFormData({...formData, stock: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Unit Value (₹)</label>
              <input required type="number" min="0" value={formData.price} onChange={e=>setFormData({...formData, price: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Configuration</button>
        </form>
      </div>
    </div>
  );
};

const EditInventoryModal = ({ onClose, onEdit, item }) => {
  const [formData, setFormData] = useState(item);
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl overflow-y-auto max-h-[90vh]">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-gold transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-gold mb-6">Edit Item</h2>
        <form onSubmit={(e) => { e.preventDefault(); onEdit(formData); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Product Name</label>
            <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Replace Image (Optional)</label>
            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setFormData)} className="w-full bg-card_bg border border-border_soft rounded-xl p-2 text-text_primary focus:ring-1 focus:ring-primary/50 text-sm" />
            {formData.image_url && <img src={formData.image_url} alt="Preview" className="h-16 mt-3 rounded-md border border-border_soft/50 shadow-sm" />}
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Barcode / SKU</label>
            <input type="text" value={formData.barcode || ""} onChange={e=>setFormData({...formData, barcode: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary font-mono focus:ring-1 focus:ring-primary/50" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Current Reserves</label>
              <input required type="number" min="0" value={formData.stock} onChange={e=>setFormData({...formData, stock: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Unit Value (₹)</label>
              <input required type="number" min="0" value={formData.price} onChange={e=>setFormData({...formData, price: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Adjustments</button>
        </form>
      </div>
    </div>
  );
};

const SellItemModal = ({ onClose, onSell, item }) => {
  const [sellData, setSellData] = useState({ id: item.id, qty: 1, member: "Walk-in Customer" });
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-sm relative shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-gold transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-gold mb-2">Sell Item</h2>
        <p className="text-xs font-mono text-gold bg-primary/10 px-3 py-1.5 rounded-lg border border-primary/20 mb-6 inline-block">{item.name}</p>
        <form onSubmit={(e) => { e.preventDefault(); onSell(sellData); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Volume to Dispense</label>
            <input required type="number" min="1" max={item.stock} value={sellData.qty} onChange={e=>setSellData({...sellData, qty: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Customer Name</label>
            <input required type="text" value={sellData.member} onChange={e=>setSellData({...sellData, member: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 flex items-center justify-center gap-2 hover:bg-primary_hover transition-colors shadow-md">
            <ShoppingCart size={18}/> Sell & Bill
          </button>
        </form>
      </div>
    </div>
  );
};

const AddStockModal = ({ onClose, onAddStock }) => {
  const [addStockQty, setAddStockQty] = useState(10);
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-sm relative shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-gold transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-gold mb-6">Add Stock</h2>
        <form onSubmit={(e) => { e.preventDefault(); onAddStock(addStockQty); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Volume to Inject</label>
            <input required type="number" min="1" value={addStockQty} onChange={e=>setAddStockQty(e.target.value)} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 flex items-center justify-center gap-2 hover:bg-primary_hover transition-colors shadow-md">
            <ArrowDownToLine size={18}/> Add Stock
          </button>
        </form>
      </div>
    </div>
  );
};

export default function Inventory() {
  const [activeTab, setActiveTab] = useState("pos");
  const [items, setItems] = useState([]);
  const [register, setRegister] = useState([]);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showPrint, setShowPrint] = useState(null);
  const [showAddStockModal, setShowAddStockModal] = useState(null);
  const [showSellModal, setShowSellModal] = useState(null);

  const [scanInput, setScanInput] = useState("");
  const scanInputRef = useRef(null);

  const fetchInventory = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory`);
      setItems(res.data);
    } catch(e) { console.error(e); }
  }, []);

  const fetchRegister = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory/register`);
      setRegister(res.data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchInventory();
    if (activeTab === "register") fetchRegister();
  }, [activeTab, fetchInventory, fetchRegister]);

  const handleAddItem = async (formData) => {
    try {
      await axios.post(`${BACKEND_URL}/api/inventory`, formData);
      setShowAddModal(false);
      fetchInventory();
    } catch(e) { console.error(e); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/update/inventory/${data.id}`, data);
      setShowEdit(null);
      fetchInventory();
    } catch(e) {
      alert(e.response?.data?.detail || "Admin access required");
    }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Permanently wipe this node from the register?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/inventory/${id}`);
        fetchInventory();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
    }
  };

  const handleAddStock = async (addStockQty) => {
    try {
      await axios.post(`${BACKEND_URL}/api/inventory/${showAddStockModal}/add_stock`, { qty: parseInt(addStockQty) });
      setShowAddStockModal(null);
      fetchInventory();
      if(activeTab === "register") fetchRegister();
    } catch(e) { console.error(e); }
  };

  const handleSellItem = async (sellData) => {
    try {
      await axios.post(`${BACKEND_URL}/api/inventory/sell`, sellData);
      setShowSellModal(null);
      fetchInventory();
    } catch(e) { alert(e.response?.data?.detail || "Insufficient volume."); }
  };

  const handleBarcodeScan = async (e) => {
    e.preventDefault();
    if (!scanInput) return;
    try {
      await axios.post(`${BACKEND_URL}/api/inventory/sell`, { barcode: scanInput, qty: 1, member: "Walk-in Customer" });
      setScanInput("");
      fetchInventory();
      alert("Sale recorded!");
    } catch(e) {
      alert(e.response?.data?.detail || "Unknown trace or zero volume.");
      setScanInput("");
    }
    scanInputRef.current?.focus();
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 relative">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Inventory</h1>
          <p className="text-text_secondary text-sm">Manage stock, barcodes and quick sales.</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md">
          <Plus size={20} /> Add Item
        </button>
      </div>

      <div className="flex gap-4 border-b border-border_soft/30 pb-3 flex-wrap">
        <button onClick={() => setActiveTab('pos')} className={`flex items-center gap-2.5 px-5 py-2.5 font-bold rounded-xl transition-all ${activeTab === 'pos' ? 'bg-primary/10 text-gold shadow-glow border border-primary/30' : 'text-text_secondary hover:text-gold hover:bg-card_bg'}`}>
          <ShoppingCart size={18} strokeWidth={2.5}/> Point of Sale
        </button>
        <button onClick={() => setActiveTab('register')} className={`flex items-center gap-2.5 px-5 py-2.5 font-bold rounded-xl transition-all ${activeTab === 'register' ? 'bg-primary/10 text-gold shadow-glow border border-primary/30' : 'text-text_secondary hover:text-gold hover:bg-card_bg'}`}>
          <List size={18} strokeWidth={2.5}/> Central Register
        </button>
      </div>

      {activeTab === 'pos' && (
        <>
          <div className="bg-card_bg border border-border_soft p-6 rounded-2xl shadow-sm mb-8">
            <h3 className="text-sm font-bold text-gold mb-4 uppercase tracking-widest flex items-center gap-2.5"><ScanBarcode size={18} /> Quick Scan POS</h3>
            <form onSubmit={handleBarcodeScan} className="flex gap-4 items-center flex-col sm:flex-row">
              <input 
                ref={scanInputRef}
                type="text" 
                value={scanInput} 
                onChange={(e) => setScanInput(e.target.value)} 
                placeholder="Scan barcode to sell 1 unit..." 
                className="w-full bg-main_bg border border-border_soft rounded-xl p-4 text-text_primary font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all shadow-inner"
                autoFocus
              />
              <button type="submit" className="w-full sm:w-auto bg-primary text-[#111827] font-bold px-8 py-4 rounded-xl shadow-md hover:bg-primary_hover transition-colors">Engage</button>
            </form>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.length === 0 && <p className="text-text_secondary col-span-full">No items yet.</p>}
            {items.map(item => (
              <div key={item.id} className="bg-card_bg border border-border_soft p-8 rounded-2xl flex flex-col justify-between shadow-sm relative group transition-all duration-300 hover:border-primary/40 hover:-translate-y-1">
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-card_bg/90 backdrop-blur-sm p-1.5 rounded-lg border border-border_soft/50 shadow-sm">
                  <button onClick={() => setShowPrint(item)} className="p-1.5 text-text_secondary hover:text-gold transition-colors" title="Print Barcode"><Printer size={16}/></button>
                  <button onClick={() => setShowEdit(item)} className="p-1.5 text-text_secondary hover:text-gold transition-colors" title="Edit"><Pencil size={16}/></button>
                  <button onClick={() => handleDelete(item.id)} className="p-1.5 text-text_secondary hover:text-accent_danger transition-colors" title="Delete"><Trash2 size={16}/></button>
                </div>
                
                {item.image_url ? (
                  <div className="h-32 w-full mb-6 rounded-xl overflow-hidden border border-border_soft/20 bg-main_bg">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="h-32 w-full mb-6 rounded-xl bg-main_bg flex flex-col items-center justify-center border border-border_soft/20 text-text_secondary/40">
                    <ImageIcon size={28} className="mb-2 opacity-50"/>
                    <span className="text-[9px] uppercase tracking-[0.2em] font-bold">No Image</span>
                  </div>
                )}

                <div>
                  <h3 className="text-xl font-bold text-text_primary mb-2 pr-20 leading-tight">{item.name}</h3>
                  <div className="flex items-center gap-2 mb-4">
                    <ScanBarcode size={12} className="text-text_secondary"/>
                    <p className="text-text_secondary text-[10px] font-mono uppercase tracking-widest">{item.barcode || item.id}</p>
                  </div>
                  <p className="text-gold font-bold text-3xl tracking-tight mb-6">₹{item.price.toLocaleString()}</p>
                </div>
                <div className="bg-main_bg rounded-xl p-4 border border-border_soft/20 mb-6 flex justify-between items-center shadow-inner">
                  <span className="text-xs text-text_secondary uppercase tracking-widest font-bold">Reserves</span>
                  <span className={`text-xl font-bold ${item.stock < 10 ? 'text-accent_danger' : 'text-text_primary'}`}>{item.stock}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setShowAddStockModal(item.id)} className="w-full bg-card_bg border border-border_soft text-text_secondary py-3 rounded-xl font-bold text-sm hover:text-gold hover:border-primary transition-colors shadow-sm">
                    Inject Stock
                  </button>
                  <button onClick={() => setShowSellModal(item)} className="w-full bg-primary text-[#111827] py-3 rounded-xl font-bold text-sm hover:bg-primary_hover transition-colors flex justify-center items-center gap-2 shadow-md">
                    <ShoppingCart size={16} /> Sell
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'register' && (
        <div className="bg-card_bg border border-border_soft rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-main_bg border-b border-border_soft/50">
                <tr>
                  <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Timestamp</th>
                  <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Item</th>
                  <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Type</th>
                  <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Volume</th>
                  <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Customer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border_gold/20">
                {register.length === 0 && <tr><td colSpan="5" className="px-8 py-8 text-text_secondary text-center italic">Register is pristine.</td></tr>}
                {register.map((entry, i) => (
                  <tr key={entry.id || `reg-${i}`} className="hover:bg-main_bg/50 transition-colors">
                    <td className="px-8 py-5 text-text_secondary text-xs font-mono">{new Date(entry.date).toLocaleString()}</td>
                    <td className="px-8 py-5 text-text_primary font-medium">{entry.item_name}</td>
                    <td className="px-8 py-5">
                      <span className={`flex items-center gap-1.5 w-fit px-3 py-1.5 text-[10px] font-bold uppercase rounded-lg border ${entry.type==='IN' ? 'bg-accent_success/10 border-accent_success/30 text-accent_success' : 'bg-accent_danger/10 border-accent_danger/30 text-accent_danger'}`}>
                        {entry.type==='IN' ? <ArrowDownToLine size={12}/> : <ArrowUpFromLine size={12}/>}
                        {entry.type}
                      </span>
                    </td>
                    <td className="px-8 py-5 text-text_primary font-bold">{entry.qty}</td>
                    <td className="px-8 py-5 text-text_secondary text-sm">{entry.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddModal && <AddInventoryModal onClose={() => setShowAddModal(false)} onAdd={handleAddItem} />}
      {showEdit && <EditInventoryModal item={showEdit} onClose={() => setShowEdit(null)} onEdit={handleEdit} />}
      {showSellModal && <SellItemModal item={showSellModal} onClose={() => setShowSellModal(null)} onSell={handleSellItem} />}
      {showAddStockModal && <AddStockModal onClose={() => setShowAddStockModal(null)} onAddStock={handleAddStock} />}
      {showPrint && <PrintStickerModal item={showPrint} onClose={() => setShowPrint(null)} />}
    </div>
  );
}
